/**
 * Background Changer Action
 * Triggered by: arc:action:backgroundChanger
 * 
 * This action allows you to change the background color and text color of elements
 * matching specific CSS selectors to provided hex colors.
 */

export const runBackgroundChanger = async (data = {}) => {
    try {
        const {
            selectors = [],
            background = '#ffffff',
            textColor = null,
            transition = '0.3s ease',
            important = false,
            resetOthers = false,
            originalColors = new Map(),
            excludeTextColor = [],
            excludeBackground = []
        } = data;

        // Helper function to check if a selector matches a pattern (with wildcard support)
        const matchesPattern = (selector, pattern) => {
            if (!pattern.includes('*')) {
                return selector === pattern; // Exact match
            }
            
            // Convert wildcard pattern to regex
            const regexPattern = pattern
                .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape special regex chars except *
                .replace(/\*/g, '.*'); // Replace * with .*
            
            const regex = new RegExp(`^${regexPattern}$`);
            return regex.test(selector);
        };

        // Helper function to check if a selector should be excluded
        const isExcluded = (selector, exclusionList) => {
            return exclusionList.some(pattern => matchesPattern(selector, pattern));
        };

        // Helper function to check if an element matches any exclusion selector (including descendant selectors)
        const isElementExcluded = (element, exclusionList) => {
            return exclusionList.some(pattern => {
                try {
                    // For wildcard patterns, we need to check if the element matches the pattern
                    if (pattern.includes('*')) {
                        // Get all possible selectors for this element
                        const elementSelectors = [];
                        if (element.id) elementSelectors.push(`#${element.id}`);
                        if (element.className) {
                            element.classList.forEach(cls => elementSelectors.push(`.${cls}`));
                        }
                        elementSelectors.push(element.tagName.toLowerCase());
                        
                        // Check if any of the element's selectors match the wildcard pattern
                        return elementSelectors.some(sel => matchesPattern(sel, pattern));
                    } else {
                        // For regular CSS selectors, use matches()
                        return element.matches(pattern);
                    }
                } catch (error) {
                    // If selector is invalid, fall back to false
                    return false;
                }
            });
        };

        // Helper function to check if an element is inside an excluded container
        const isElementInExcludedContainer = (element, exclusionList) => {
            let currentElement = element.parentElement;
            while (currentElement && currentElement !== document.body) {
                if (isElementExcluded(currentElement, exclusionList)) {
                    return true;
                }
                currentElement = currentElement.parentElement;
            }
            return false;
        };

        // Helper function to reset text color override for excluded elements and their descendants
        const resetTextColorOverride = (element) => {
            // Get the original color scheme color by finding the color scheme class
            const getOriginalSchemeColor = (el) => {
                // Find the color scheme class on the element or its ancestors
                let colorSchemeElement = el.closest('[class*="color-"]');
                if (!colorSchemeElement) {
                    // If no color scheme found, try to get from body or html
                    colorSchemeElement = document.body.querySelector('[class*="color-"]') || document.body;
                }
                
                // Extract the color scheme class name
                const classNames = colorSchemeElement.className.split(' ');
                const colorSchemeClass = classNames.find(className => className.startsWith('color-'));
                
                if (colorSchemeClass) {
                    // Create a clean test element with just the color scheme class
                    const temp = document.createElement('div');
                    temp.className = colorSchemeClass;
                    temp.style.visibility = 'hidden';
                    temp.style.position = 'absolute';
                    temp.style.pointerEvents = 'none';
                    temp.style.setProperty('--text-color-override', 'initial');
                    document.body.appendChild(temp);
                    
                    const originalColor = window.getComputedStyle(temp).color;
                    document.body.removeChild(temp);
                    
                    return originalColor;
                }
                
                // Fallback: return computed color without override
                return window.getComputedStyle(el).color;
            };
            
            // Remove the override and restore original color
            element.style.removeProperty('--text-color-override');
            
            // Only set explicit color if the element has a color scheme class
            if (element.className.includes('color-')) {
                const originalColor = getOriginalSchemeColor(element);
                element.style.setProperty('color', originalColor, 'important');
            }
            
            // Reset for all child elements with color scheme classes
            const childrenWithColorSchemes = element.querySelectorAll('[class*="color-"]');
            childrenWithColorSchemes.forEach(child => {
                child.style.removeProperty('--text-color-override');
                const childOriginalColor = getOriginalSchemeColor(child);
                child.style.setProperty('color', childOriginalColor, 'important');
            });
        };

        // Validate hex color format
        const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
        if (!hexPattern.test(background)) {
            throw new Error('Invalid hex color format. Please use format #FFFFFF or #FFF');
        }

        // Validate text color format if provided
        if (textColor && !hexPattern.test(textColor)) {
            throw new Error('Invalid text color format. Please use format #FFFFFF or #FFF');
        }

        // Ensure selectors is an array
        const selectorArray = Array.isArray(selectors) ? selectors : [selectors];
        
        if (selectorArray.length === 0) {
            throw new Error('At least one CSS selector must be provided');
        }

        const changedElements = [];
        const failedSelectors = [];

        // Store original background colors if not already stored
        if (resetOthers && originalColors.size === 0) {
            selectorArray.forEach(selector => {
                try {
                    const elements = document.querySelectorAll(selector);
                    elements.forEach(element => {
                        if (!originalColors.has(element)) {
                            const computedStyle = window.getComputedStyle(element);
                            originalColors.set(element, {
                                backgroundColor: computedStyle.backgroundColor,
                                backgroundImage: computedStyle.backgroundImage,
                                color: computedStyle.color
                            });
                        }
                    });
                } catch (error) {
                    // console.warn(`Failed to store original colors for selector: ${selector}`, error);
                }
            });
        }

        // Apply background changes to each selector
        for (const selector of selectorArray) {
            try {
                const elements = document.querySelectorAll(selector);
                
                // console.log(`Searching for selector: "${selector}" - Found ${elements.length} elements`);
                
                if (elements.length === 0) {
                    failedSelectors.push({
                        selector,
                        reason: 'No elements found'
                    });
                    continue;


                }

                elements.forEach((element, index) => {
                    // console.log(`Processing element ${index + 1} for selector "${selector}":`, element);
                    
                    // Check if this selector is excluded from background or text color changes
                    // Check original selector, element itself, and if element is inside an excluded container
                    const shouldApplyBackground = !isExcluded(selector, excludeBackground) && 
                                                !isElementExcluded(element, excludeBackground) &&
                                                !isElementInExcludedContainer(element, excludeBackground);
                    
                    const shouldApplyTextColor = textColor && 
                                               !isExcluded(selector, excludeTextColor) && 
                                               !isElementExcluded(element, excludeTextColor) &&
                                               !isElementInExcludedContainer(element, excludeTextColor);
                    
                    // If this element is excluded from text color changes or is inside an excluded container,
                    // make sure to reset any inherited text color overrides
                    if (!shouldApplyTextColor && (isElementExcluded(element, excludeTextColor) || isElementInExcludedContainer(element, excludeTextColor))) {
                        resetTextColorOverride(element);
                    }
                    
                    // Store original styles for debugging
                    const originalStyle = window.getComputedStyle(element);
                    // console.log(`Original background-color: ${originalStyle.backgroundColor}`);
                    // console.log(`Original background-image: ${originalStyle.backgroundImage}`);
                    // console.log(`Original color: ${originalStyle.color}`);
                    
                    // Add transition if specified
                    if (transition && (shouldApplyBackground || shouldApplyTextColor)) {
                        const transitionProperties = [];
                        if (shouldApplyBackground) transitionProperties.push(`background-color ${transition}`);
                        if (shouldApplyTextColor) transitionProperties.push(`color ${transition}`);
                        element.style.transition = transitionProperties.join(', ');
                    }

                    // Apply the background color with multiple methods for maximum compatibility
                    if (important) {
                        if (shouldApplyBackground) {
                            element.style.setProperty('background-color', background, 'important');
                            element.style.setProperty('background-image', 'none', 'important');
                        }
                        if (shouldApplyTextColor) {
                            element.style.setProperty('color', textColor, 'important');
                        }
                        // Also try direct style assignment as fallback
                        let cssText = '';
                        if (shouldApplyBackground) {
                            cssText += `; background-color: ${background} !important; background-image: none !important;`;
                        }
                        if (shouldApplyTextColor) {
                            cssText += ` color: ${textColor} !important;`;
                        }
                        if (cssText) {
                            element.style.cssText += cssText;
                        }
                    } else {
                        if (shouldApplyBackground) {
                            element.style.backgroundColor = background;
                            element.style.backgroundImage = 'none';
                        }
                        if (shouldApplyTextColor) {
                            element.style.color = textColor;
                        }
                    }

                    // Set CSS variables to override styles from color schemes
                    if (shouldApplyTextColor) {
                        element.style.setProperty('--text-color-override', textColor);
                        
                        // However, if this element contains any excluded child elements,
                        // we need to reset the override for those children
                        const excludedChildren = element.querySelectorAll('*');
                        excludedChildren.forEach(child => {
                            if (isElementExcluded(child, excludeTextColor) || isElementInExcludedContainer(child, excludeTextColor)) {
                                resetTextColorOverride(child);
                            }
                        });
                    }
                    
                    // Force a reflow to ensure the change is applied
                    element.offsetHeight;
                    
                    // Verify the change was applied
                    const newStyle = window.getComputedStyle(element);
                    // console.log(`New background-color: ${newStyle.backgroundColor}`);
                    // console.log(`New color: ${newStyle.color}`);
                    
                    changedElements.push({
                        element,
                        selector,
                        appliedColor: shouldApplyBackground ? background : null,
                        appliedTextColor: shouldApplyTextColor ? textColor : null,
                        backgroundExcluded: !shouldApplyBackground,
                        textColorExcluded: !shouldApplyTextColor,
                        elementTag: element.tagName,
                        elementClasses: element.className,
                        elementId: element.id,
                        originalBackgroundColor: originalStyle.backgroundColor,
                        originalTextColor: originalStyle.color,
                        newBackgroundColor: newStyle.backgroundColor,
                        newTextColor: newStyle.color
                    });
                });

            } catch (error) {
                // console.error(`Error processing selector "${selector}":`, error);
                failedSelectors.push({
                    selector,
                    reason: error.message
                });
            }
        }

        // Post-processing: Ensure all excluded elements and their descendants are properly reset
        excludeTextColor.forEach(pattern => {
            try {
                // Handle wildcard patterns
                if (pattern.includes('*')) {
                    // Find all elements that might match the wildcard pattern
                    const allElements = document.querySelectorAll('*');
                    allElements.forEach(element => {
                        if (isElementExcluded(element, [pattern])) {
                            resetTextColorOverride(element);
                        }
                    });
                } else {
                    // Handle regular CSS selectors
                    const excludedElements = document.querySelectorAll(pattern);
                    excludedElements.forEach(element => {
                        resetTextColorOverride(element);
                    });
                }
            } catch (error) {
                // Invalid selector, skip
                console.warn(`Invalid exclusion selector: ${pattern}`, error);
            }
        });

        // Reset other elements if requested
        if (resetOthers && originalColors.size > 0) {
            originalColors.forEach((originalStyle, element) => {
                // Only reset if this element wasn't in our target selectors
                const isTargeted = changedElements.some(changed => changed.element === element);
                if (!isTargeted) {
                    element.style.backgroundColor = originalStyle.backgroundColor;
                    element.style.backgroundImage = originalStyle.backgroundImage;
                    element.style.color = originalStyle.color;
                }
            });
        }

        const result = {
            success: true,
            message: `Background color${textColor ? ' and text color' : ''} applied to ${changedElements.length} elements`,
            data: {
                background,
                textColor,
                changedElements: changedElements.length,
                successfulSelectors: selectorArray.filter(s => !failedSelectors.find(f => f.selector === s)),
                failedSelectors,
                appliedTransition: transition
            }
        };

        // Log success for debugging
        // console.log('Background Changer Success:', {
        //     color: background,
        //     textColor: textColor,
        //     elementsChanged: changedElements.length,
        //     selectors: selectorArray,
        //     changedElements: changedElements.map(el => ({
        //         selector: el.selector,
        //         elementTag: el.elementTag,
        //         elementClasses: el.elementClasses,
        //         elementId: el.elementId,
        //         originalBackgroundColor: el.originalBackgroundColor,
        //         originalTextColor: el.originalTextColor,
        //         newBackgroundColor: el.newBackgroundColor,
        //         newTextColor: el.newTextColor
        //     })),
        //     failedSelectors
        // });

        // Also log a simple verification
        if (changedElements.length === 0) {
            // console.warn('⚠️ No elements were changed! Check if your selectors match existing elements.');
            console.log('Available elements to debug:', {
                allDivs: document.querySelectorAll('div').length,
                allSections: document.querySelectorAll('section').length,
                elementsWithHeroClass: document.querySelectorAll('.hero').length,
                elementsWithBannerClass: document.querySelectorAll('.banner').length
            });
        }

        return result;

    } catch (error) {
        // console.error('Background Changer Error:', error);
        return {
            success: false,
            error: error.message,
            data: {
                providedData: data
            }
        };
    }
};

export default runBackgroundChanger;

// Helper function for testing and debugging
export const testBackgroundChanger = () => {
    // console.log('Testing Background Changer...');
    
    // Test with common selectors including text color
    window.dispatchEvent(new CustomEvent('arc:action:backgroundChanger', {
        detail: {
            selectors: ['body', 'div', 'section', '.hero-section', '.banner'],
            background: '#ff0000',
            textColor: '#ffffff',
            transition: '0.3s ease'
        }
    }));
    
    // Also try direct function call for immediate testing
    runBackgroundChanger({
        selectors: ['body'],
        background: '#00ff00',
        textColor: '#000000',
        transition: '0.3s ease'
    }).then(result => {
        // console.log('Direct function call result:', result);
    });
};